import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { TrendingDown, PhoneOff, MoonStar, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const DEFAULT_STATS = [
  {
    icon: PhoneOff,
    stat: "85%",
    label: "of callers who can't reach you never call back",
  },
  {
    icon: MoonStar,
    stat: "27%",
    label: "of service calls come in after business hours",
  },
  {
    icon: TrendingDown,
    stat: "$1,200+",
    label: "average ticket walking to a competitor",
  },
];

export default function Calculator({
  stats = DEFAULT_STATS,
  defaultMissed = 12,
  defaultTicket = 1200,
  ticketMax = 5000,
  ticketStep = 50,
}) {
  const [missed, setMissed] = useState(defaultMissed);
  const [ticket, setTicket] = useState(defaultTicket);
  const [reportEmail, setReportEmail] = useState("");
  const [sending, setSending] = useState(false);

  const annualLeak = missed * 52 * ticket * 0.62;
  const recovered = annualLeak * 0.4;

  const sendReport = async (e) => {
    e.preventDefault();
    if (!reportEmail.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/calculator/report`, {
        email: reportEmail.trim(),
        missed_calls: missed,
        avg_ticket: ticket,
        source: window.location.pathname,
      });
      toast.success("Report on its way", {
        description: "Your revenue-leak numbers are headed to your inbox.",
      });
      setReportEmail("");
    } catch (err) {
      toast.error("Couldn't send the report", {
        description: err.response?.data?.detail || "Try again in a moment.",
      });
    } finally {
      setSending(false);
    }
  };

  const fill = (v, min, max) => `${((v - min) / (max - min)) * 100}%`;

  return (
    <section id="problem" data-testid="calculator-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>The Problem</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Your phones leak revenue every single week
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-400">
            When a tech is on a job and the office is at lunch, the phone
            rings out. Homeowners don&apos;t leave voicemail — they call the
            next contractor on Google.
          </p>
        </Reveal>

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {stats.map((s, i) => (
            <Reveal key={s.stat} delay={i * 0.08}>
              <div
                data-testid={`problem-stat-${i}`}
                className="h-full rounded-lg border border-white/[0.08] bg-[#12161F] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#F25F22]/40"
              >
                <s.icon className="h-5 w-5 text-[#F25F22]" />
                <p className="mt-4 font-display text-3xl font-bold text-slate-50">
                  {s.stat}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  {s.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="leak-calculator"
            className="mt-6 grid gap-10 rounded-xl border border-white/[0.08] bg-[#12161F] p-7 sm:p-10 lg:grid-cols-2"
          >
            <div className="space-y-9">
              <div>
                <div className="flex items-baseline justify-between">
                  <label
                    htmlFor="missed-calls-slider"
                    className="text-sm font-semibold text-slate-200"
                  >
                    Missed calls per week
                  </label>
                  <span
                    data-testid="missed-calls-value"
                    className="font-mono text-lg font-semibold text-[#F25F22]"
                  >
                    {missed}
                  </span>
                </div>
                <input
                  id="missed-calls-slider"
                  data-testid="missed-calls-slider"
                  type="range"
                  min="1"
                  max="50"
                  value={missed}
                  style={{ "--fill": fill(missed, 1, 50) }}
                  onChange={(e) => setMissed(Number(e.target.value))}
                  className="mt-4"
                />
              </div>
              <div>
                <div className="flex items-baseline justify-between">
                  <label
                    htmlFor="ticket-slider"
                    className="text-sm font-semibold text-slate-200"
                  >
                    Average job ticket
                  </label>
                  <span
                    data-testid="ticket-value"
                    className="font-mono text-lg font-semibold text-[#F25F22]"
                  >
                    {money(ticket)}
                  </span>
                </div>
                <input
                  id="ticket-slider"
                  data-testid="ticket-slider"
                  type="range"
                  min="200"
                  max={ticketMax}
                  step={ticketStep}
                  value={ticket}
                  style={{ "--fill": fill(ticket, 200, ticketMax) }}
                  onChange={(e) => setTicket(Number(e.target.value))}
                  className="mt-4"
                />
              </div>
              <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500">
                Assumes 62% of missed callers would have booked · Backfill
                recovers ~40%
              </p>
            </div>

            <div className="flex flex-col justify-center gap-6 lg:border-l lg:border-white/[0.08] lg:pl-10">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    Revenue leaking per year
                </p>
                <p
                  data-testid="annual-leak-value"
                  className="mt-2 font-display text-4xl font-bold text-red-400 sm:text-5xl"
                >
                  {money(annualLeak)}
                </p>
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/[0.06]">
                  <div className="h-full w-full rounded-full bg-red-400/70" />
                </div>
              </div>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                  Recoverable with Backfill
                </p>
                <p
                  data-testid="recovered-value"
                  className="mt-2 font-display text-4xl font-bold text-emerald-400 sm:text-5xl"
                >
                  {money(recovered)}
                </p>
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/[0.06]">
                  <div
                    className="h-full rounded-full bg-emerald-400 transition-all duration-500"
                    style={{ width: "40%" }}
                  />
                </div>
              </div>
              <form onSubmit={sendReport} className="border-t border-white/[0.08] pt-6">
                <label
                  htmlFor="calc-report-email"
                  className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500"
                >
                  Email me this report
                </label>
                <div className="mt-3 flex gap-2">
                  <input
                    id="calc-report-email"
                    type="email"
                    required
                    value={reportEmail}
                    onChange={(e) => setReportEmail(e.target.value)}
                    data-testid="calculator-report-email-input"
                    placeholder="you@yourcompany.com"
                    className="w-full min-w-0 rounded-md border border-white/[0.1] bg-[#0B0D10] px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-[#F25F22]/60 focus:ring-2 focus:ring-[#F25F22]/20"
                  />
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="calculator-report-button"
                    className="flex shrink-0 items-center gap-2 rounded-md bg-[#F25F22] px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#FF7539] disabled:opacity-60"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send
                  </button>
                </div>
                <p className="mt-2 text-xs text-slate-600">
                  Your numbers, in your inbox — no spam, ever.
                </p>
              </form>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
